import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  class: "swiperWrapper datepickerCarousel",
  "aria-label": "Choose a date"
}

import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { ref, watch } from 'vue';
import { formatInTimeZone } from 'date-fns-tz';
import { isValid, parseISO } from 'date-fns';
import { timezone } from '../../envVariables';
import { CarouselDateItem } from '../../Atoms';
import { CarouselDateItemProps, CarouselDateSelectionInfo } from '../../types';
import { addDays, utcDate, differenceInCalendarDays } from '../..';

import 'swiper/css';
import 'swiper/css/navigation';

const slidesOnOneSide = 10;


export default /*@__PURE__*/_defineComponent({
  __name: 'DatePickerCarousel',
  props: {
    disabled: { type: Boolean },
    datesSelectableBeforeToday: {},
    initialDate: {},
    reinitialiseSwiper: { type: Boolean },
    isLoading: { type: Boolean },
    sailingAvailability: {}
  },
  emits: ['updateDate', 'toggleInitSwiper'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const swiperSlides = ref<typeof Swiper>();
const dates = ref(Array<CarouselDateItemProps>());
const date = ref<string>(props.initialDate);
const datesVisibleBeforeToday = ref(0);
const reinitSwiper = ref(props.reinitialiseSwiper);
const today = utcDate(new Date()).toISOString();
const isSlideDisabled = (slideDate) => (today > slideDate) || props.disabled || false;

let activeIndex;
const createSlide = (slideDate: string, isDisabled: boolean) => {
  const dateKey = slideDate.split('T')[0];
  const hasSailings = props.sailingAvailability?.[dateKey];
  return {
    active: +slideDate === +date.value,
    disabled: isDisabled,
    dayOfWeek: formatInTimeZone(slideDate, timezone, 'ccc'),
    fullDayOfWeek: formatInTimeZone(slideDate, timezone, 'cccc'),
    dayOfMonth: formatInTimeZone(slideDate, timezone, 'd'),
    month: formatInTimeZone(slideDate, timezone, 'MMM'),
    fullMonth: formatInTimeZone(slideDate, timezone, 'MMMM'),
    carouselItemDate: slideDate,
    hasSailings,
  };
};

const createSlides = (firstDay, totalSlides) => Array.from(
  { length: totalSlides },
  (_, i: number) => {
    const slideDate = addDays(firstDay, i);
    return createSlide(slideDate, isSlideDisabled(slideDate));
  },
);

const silentSlideToActive = () => {
  swiperSlides.value?.slideTo(activeIndex, 0, false);
};

const setNewActiveState = () => {
  dates.value = dates.value.map((d) => ({ ...d, active: false }));
  dates.value[activeIndex].active = true;
};

const initSlides = (newDate) => {
  const daysSinceToday = differenceInCalendarDays(today, newDate);

  const daysOnLeftSide = Math.min(
    daysSinceToday + datesVisibleBeforeToday.value,
    slidesOnOneSide,
  );

  const firstDay = addDays(newDate, -daysOnLeftSide);
  const totalDays = daysOnLeftSide + 1 + slidesOnOneSide;

  activeIndex = daysOnLeftSide;
  dates.value = createSlides(firstDay, totalDays);

  setNewActiveState();
  silentSlideToActive();

  reinitSwiper.value = false;
};

const handleKeyPress = (event: KeyboardEvent, button: HTMLElement) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    button.click();
  }
};

const enhanceNavigationAccessibility = (swiper) => {
  swiper.on('afterInit', () => {
    const prevButton = swiper.el.querySelector('.swiper-button-prev');
    const nextButton = swiper.el.querySelector('.swiper-button-next');

    if (prevButton) {
      prevButton.setAttribute('tabindex', '0');
      prevButton.setAttribute('role', 'button');
      prevButton.setAttribute('aria-label', 'Previous slide');
      prevButton.addEventListener('keydown', (e) => handleKeyPress(e, prevButton));
    }

    if (nextButton) {
      nextButton.setAttribute('tabindex', '0');
      nextButton.setAttribute('role', 'button');
      nextButton.setAttribute('aria-label', 'Next slide');
      nextButton.addEventListener('keydown', (e) => handleKeyPress(e, nextButton));
    }
  });
};

const onSwiper = (swiper) => {
  swiperSlides.value = swiper;

  switch (swiper.currentBreakpoint) {
    case 'max':
      datesVisibleBeforeToday.value = 1;
      break;
    case '420':
      datesVisibleBeforeToday.value = 2;
      break;
    default:
      datesVisibleBeforeToday.value = 3;
  }

  initSlides(date.value);
  enhanceNavigationAccessibility(swiper);
};

const getDateSelectionInfo = (centerIndex: number, swiper: any): CarouselDateSelectionInfo => {
  const { slidesPerView } = swiper.params;
  const visibleDays = Math.floor(slidesPerView / 2);
  const bufferDays = 5;
  const daysToLoad = visibleDays + bufferDays;
  return {
    selectedDate: dates.value[centerIndex].carouselItemDate,
    daysToLoad,
  };
};

const onSlideChangeTransitionEnd = (swiper) => {
  initSlides(dates.value[swiper.realIndex].carouselItemDate);

  if (reinitSwiper.value) {
    emit('toggleInitSwiper', false);
  } else {
    const dateInfo = getDateSelectionInfo(swiper.realIndex, swiper);
    emit('updateDate', dateInfo);
  }

  reinitSwiper.value = false;
};

function handleDateClick(index: number) {
  dates.value = dates.value.map((d, i) => ({
    ...d,
    active: i === index,
  }));
  swiperSlides.value?.slideTo(index);
  const dateInfo = getDateSelectionInfo(index, swiperSlides.value);
  emit('updateDate', dateInfo);
}

watch([() => props.initialDate, () => props.reinitialiseSwiper], ([newDate, newReinit]) => {
  if (!isValid(parseISO(newDate))) {
    return;
  }

  if (newDate) date.value = newDate;
  if (newReinit) reinitSwiper.value = newReinit;

  if (reinitSwiper.value) {
    initSlides(newDate);
  }
});

watch(() => props.sailingAvailability, (newAvailability) => {
  if (newAvailability && dates.value.length > 0) {
    dates.value = dates.value.map((dateItem) => {
      const dateKey = dateItem.carouselItemDate.split('T')[0];
      const hasSailings = newAvailability[dateKey];
      return {
        ...dateItem,
        hasSailings,
      };
    });
  }
}, { immediate: true, deep: true });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Swiper), {
      "slides-per-view": 3,
      "space-between": 5,
      speed: 300,
      navigation: "",
      modules: [_unref(Navigation)],
      breakpoints: {
        '420': {
          slidesPerView: 5,
          spaceBetween: 10,
        },
        '1264': {
          slidesPerView: 7,
          spaceBetween: 0,
        },
      },
      "slide-to-clicked-slide": true,
      "centered-slides": true,
      "centered-slides-bounds": true,
      class: _normalizeClass({ disabled: _ctx.disabled }),
      onSwiper: onSwiper,
      onSlideChangeTransitionEnd: onSlideChangeTransitionEnd
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dates.value, (itemDate, index) => {
          return (_openBlock(), _createBlock(_unref(SwiperSlide), { key: index }, {
            default: _withCtx(() => [
              _createVNode(_unref(CarouselDateItem), {
                active: itemDate.active,
                disabled: itemDate.disabled,
                "day-of-week": itemDate.dayOfWeek,
                "full-day-of-week": itemDate.fullDayOfWeek,
                "day-of-month": itemDate.dayOfMonth,
                month: itemDate.month,
                "full-month": itemDate.fullMonth,
                "is-loading": _ctx.isLoading,
                "has-sailings": itemDate.hasSailings,
                "on-click": () => handleDateClick(index)
              }, null, 8, ["active", "disabled", "day-of-week", "full-day-of-week", "day-of-month", "month", "full-month", "is-loading", "has-sailings", "on-click"])
            ]),
            _: 2
          }, 1024))
        }), 128))
      ]),
      _: 1
    }, 8, ["modules", "class"])
  ]))
}
}

})